<template>
  <div class="p-text-field-formatted">
    <p-text-field
      v-model="textFieldValue"
      v-bind="$props"
      v-on="{...$listeners, input: onInput}"
      @blur="onBlur"
      @focus="onFocus"
      @clear="onClearClicked"
    >
      <template v-for="(_, slot) of $scopedSlots" #[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
    </p-text-field>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import PTextField from '../p-text-field/p-text-field.vue';

  export default Vue.extend({
    props: {
      ...((new PTextField()).$options.props),
      value: { type: undefined, default: undefined },
      /** Format function that describes what the users sees */
      displayFormatter: { type: Function, default: undefined },
      /** Format function that describes what the component effectively emits as value */
      valueFormatter: { type: Function, default: undefined },
      /** Format function that describes what the component effectively emits as value */
      userInputFormatter: { type: Function, default: undefined },
      /** Format while the user is typing and not just on blur */
      formatWhileTyping: { type: Boolean, default: false },
      /** @deprecated use setDisplayForUserInput instead */
      setDisplayForUser: { type: Boolean, default: false },
      /** Overwrite the user-input with the formatted display value on blur
       * (this prop has no effect when updateValueWhileTyping is set) */
      setDisplayForUserInput: { type: Boolean, default: false },
      /** Emit input event while typing */
      updateValueWhileTyping: { type: Boolean, default: false },
    },
    data: () => ({
      isInEditMode: false,
      displayValue: undefined as any,
      userInput: undefined as any,
      ignoreValueChange: false,
      innerErrors: undefined as string | string[] | undefined,
      innerMessages: undefined as string | string[] | undefined,

    }),
    computed: {
      textFieldValue: {
        get(): any {
          return this.isInEditMode ? this.userInput : this.displayValue;
        },
        set(value: any) {
          this.userInput = value;
        },
      },
      hasErrors() {
        return this.innerErrors && this.innerErrors.length > 0;
      },
      hasMessages() {
        return this.innerMessages && this.innerMessages.length > 0;
      },

    },
    watch: {
      value: {
        immediate: true,
        handler() {
          // This is the easiest solution for now
          // See discussion -> https://github.com/vuejs/vue/issues/1829
          if (this.ignoreValueChange) {
            this.ignoreValueChange = false;
            return;
          }
          // TODO: Give possibility to format value when it changes with a prop flag
          const display = this.formatDisplay(this.value, this.value);
          if (this.value) {
            this.formatUserInput(undefined, this.value, display);
          }
        },
      },
      isInEditMode() {
        this.$emit('edit-mode-changed', this.isInEditMode);
      },
      userValue(newValue: any) {
        this.$emit('user-input', newValue);
      },
    },
    methods: {
      onClearClicked() {
        this.userInput = undefined;
        // Force an update of all formatted data
        this.onInput();
        const formattedValue = this.formatAll(this.userInput);
        this.$emit('input', formattedValue);
        this.$emit('clear');
      },
      /** @returns Formatted value */
      formatAll(userInput: string | undefined) {
        const formattedValue = this.formatValue(userInput);
        const formattedDisplay = this.formatDisplay(userInput, formattedValue);
        this.formatUserInput(userInput, formattedValue, formattedDisplay);
        return formattedValue;
      },
      formatValue(userInput: string | undefined) {
        if (typeof this.valueFormatter === 'function') {
          const formattedValue = this.valueFormatter(userInput);
          this.$emit('input', formattedValue);
          return formattedValue;
        }
        this.$log.error('Unable to format value, valueFormatter prop is not a function');
        return undefined;
      },
      formatDisplay(userInput: string | undefined, value: string | undefined) {
        if (typeof this.displayFormatter === 'function') {
          this.displayValue = this.displayFormatter(userInput, value);
          this.$emit('display', this.displayValue);
        } else {
          this.$log.error('Unable to format display, displayFormatter prop is not a function');
          this.displayValue = value;
        }

        return this.displayValue;
      },
      formatUserInput(userInput: string | undefined, value: string | undefined, display: string | undefined) {
        if (typeof this.userInputFormatter === 'function' && !this.setDisplayForUserInput) {
          const formattedUserInput = this.userInputFormatter(userInput, value, display);
          this.$emit('user-input', formattedUserInput);
          this.userInput = formattedUserInput;
          return formattedUserInput;
        }
        if (this.setDisplayForUser || this.setDisplayForUserInput) {
          this.userInput = display;
        } else {
          this.userInput = userInput ?? value;
        }
        this.$set(this, 'userInput', this.userInput);
        this.$emit('user-input', this.userInput);
        return this.userInput;
      },
      onFocus() {
        this.isInEditMode = true;
        this.$emit('focus');
      },
      onBlur() {
        this.isInEditMode = false;
        const formattedValue = this.formatAll(this.userInput);
        this.ignoreValueChange = this.value !== formattedValue && !(this.value === '' && formattedValue === undefined);
        this.$emit('input', formattedValue);
        this.$emit('blur');
      },
      onInput() {
        if (this.formatWhileTyping) {
          this.formatDisplay(this.userInput, undefined);
          this.userInput = this.displayValue;
        }
        if (this.updateValueWhileTyping) {
          this.ignoreValueChange = true;
          this.formatValue(this.userInput);
        }
      },
    },
  });
</script>
